import { ArrowRight, CheckCircle2, PlayCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import FaqList from '../components/FaqList';
import FeatureGrid from '../components/FeatureGrid';
import HowItWorks from '../components/HowItWorks';
import ImportShowcase from '../components/ImportShowcase';
import PricingCards from '../components/PricingCards';
import Seo from '../components/Seo';
import { trustItems } from '../data/site';

export default function Home() {
  return (
    <>
      <Seo
        title="Supplier product drafting for Shopify"
        description="DropBridge helps Shopify merchants create reviewed product drafts from merchant-selected supplier details — with variants, images, and pricing imported automatically — edit listings, apply markups, and publish cleaner Shopify catalogs."
      />
      <section className="relative overflow-hidden pb-20 pt-32 sm:pt-36 lg:pb-28">
        <div className="absolute inset-0 -z-10 bg-grid bg-[size:36px_36px] opacity-40 dark:opacity-10" />
        <div className="absolute left-1/2 top-20 -z-10 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-teal-300/25 blur-3xl dark:bg-teal-400/15" />
        <div className="container-page grid items-center gap-14 lg:grid-cols-[1.02fr_0.98fr]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/80 px-3 py-1 text-sm font-medium text-teal-800 shadow-sm dark:border-teal-300/20 dark:bg-white/10 dark:text-teal-200">
              <CheckCircle2 size={16} />
              Shopify product drafting, refined
            </div>
            <h1 className="mt-7 max-w-4xl text-5xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-6xl lg:text-7xl">
              Build a better Shopify catalog from supplier product ideas.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              DropBridge helps merchants turn selected supplier details into reviewed Shopify drafts
              — variants, images, and stock status included — edit listings before they go live,
              apply price markups with confidence, and keep prices and stock in sync afterwards.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="https://apps.shopify.com/dropbridge"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                Install DropBridge
                <ArrowRight size={18} />
              </a>
              <Link
                to="/features"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition hover:-translate-y-0.5 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
              >
                <PlayCircle size={18} />
                Explore features
              </Link>
            </div>
            <div className="mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
              {trustItems.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
                >
                  <item.icon size={17} className="text-teal-600 dark:text-teal-300" />
                  {item.label}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.65 }}
            className="relative"
          >
            <div className="surface rounded-[2rem] p-5 sm:p-6">
              <ImportShowcase />
            </div>
          </motion.div>
        </div>
      </section>
      <FeatureGrid />
      <HowItWorks />
      <PricingCards />
      <FaqList />
    </>
  );
}
